import i18n from "@/i18n/i18n";
import { DatasetFilterDtoV2 } from "@/shared/dtos/Governance/dataset-filter-dto-v2";
import { DatasetFilterDtoV2SortByEnum } from "@/shared/enums/Governance/dataset-filter-dto-v2-sort-by-enum";
import { DatasetFilterDtoV2SortEnum } from "@/shared/enums/Governance/dataset-filter-dto-v2-sort-enum";
import { DatasetFilterDtoV2StatusEnum } from "@/shared/enums/Governance/dataset-filter-dto-v2-status-enum";
import { SearchService } from "@/shared/services/searchService";
import CustomStore from "devextreme/data/custom_store";
import { useCallback, useState } from "react";

export interface UseDatasetSearchResult {
	store: CustomStore;
	pageIndex: number;
	pageSize: number;
	handlePageIndexChanged: (pageIndex: number) => void;
	handlePageSizeChanged: (pageSize: number) => void;
}

const fieldMap: Record<string, DatasetFilterDtoV2SortByEnum> = {
	title: DatasetFilterDtoV2SortByEnum.TITLE,
	"catalogRecord.summary": DatasetFilterDtoV2SortByEnum.DESCRIPTION,
	"owner.name": DatasetFilterDtoV2SortByEnum.OWNER,
	"accessRights.code": DatasetFilterDtoV2SortByEnum.ACCESSRIGHTS,
	"catalogRecord.issued": DatasetFilterDtoV2SortByEnum.RECORDISSUED,
};

const applyFilter = (
	field: string,
	value: any,
	dto: DatasetFilterDtoV2,
): void => {
	if (!value) return;

	if (field === "title" || field === "catalogRecord.summary") {
		dto.text = value;
	} else if (field === "owner.name") {
		dto.owners = [value];
	} else if (field === "categories") {
		dto.categories = Array.isArray(value) ? value : [value];
	} else if (field === "accessRights.code") {
		dto.accessRights = Array.isArray(value) ? value : [value];
	}
};

export function useDatasetSearch(
	initialPageIndex = 0,
	initialPageSize = 10,
	status?: DatasetFilterDtoV2StatusEnum,
): UseDatasetSearchResult {
	const [pageIndex, setPageIndex] = useState(initialPageIndex);
	const [pageSize, setPageSize] = useState(initialPageSize);

	const createDatasetStore = () =>
		new CustomStore({
			key: "id",
			load: async (loadOptions: any) => {
				const page = loadOptions.skip / loadOptions.take || 0;
				const take = loadOptions.take;

				const dto: DatasetFilterDtoV2 = {
					offset: page * take,
					limit: take,
					status: status,
				};

				if (loadOptions.filter) {
					const filter = loadOptions.filter;

					const condicionAndOr =
						Array.isArray(filter) &&
						(filter.includes("and") || filter.includes("or"));

					if (condicionAndOr) {
						const individualFilters = filter.filter((item: any) =>
							Array.isArray(item),
						);
						for (const individualF of individualFilters) {
							const [field, , value] = individualF;
							applyFilter(field, value, dto);
						}
					} else if (Array.isArray(filter) && filter.length >= 3) {
						const [field, , value] = filter;
						applyFilter(field, value, dto);
					}
				}

				if (loadOptions.sort?.length) {
					const sortInfo = loadOptions.sort[0];
					const sortBy = fieldMap[sortInfo.selector];
					if (sortBy) {
						dto.sortBy = sortBy;
						dto.sort = sortInfo.desc
							? DatasetFilterDtoV2SortEnum.DESC
							: DatasetFilterDtoV2SortEnum.ASC;
					}
				}

				const response = await SearchService.searchRecycleDatasets(dto);

				const datasetsWithTranslation = response.datasets?.map((dataset) => ({
					...dataset,
					translatedAccessRights: dataset.accessRights?.code
						? i18n.t(
								`dataset-visibility.${dataset.accessRights.code.toLowerCase()}`,
							)
						: "",
				}));

				return {
					data: datasetsWithTranslation ?? [],
					totalCount: response.totalDatasets ?? 0,
				};
			},
		});

	const [store] = useState(createDatasetStore);

	const handlePageIndexChanged = useCallback((newPageIndex: number) => {
		setPageIndex(newPageIndex);
	}, []);

	const handlePageSizeChanged = useCallback((newPageSize: number) => {
		setPageSize(newPageSize);
		setPageIndex(0);
	}, []);

	return {
		store,
		pageIndex,
		pageSize,
		handlePageIndexChanged,
		handlePageSizeChanged,
	};
}
